import z from "zod";

const questionFeedbackSchema = z.enum(["correct", "incorrect"]);

const pupilAnswerSchema = z.union([
  z.string(),
  z.number(),
  z.array(z.union([z.string(), z.number()]).nullable()),
  z.null(),
]);

export const questionResultSchema = z.object({
  mode: z.enum(["init", "incomplete", "input", "grading", "feedback"]),
  grade: z.number(),
  pupilAnswer: pupilAnswerSchema.optional().nullable(),
  feedback: z
    .union([questionFeedbackSchema, z.array(questionFeedbackSchema)])
    .optional(),
  isPartiallyCorrect: z.boolean().optional(),
  correctAnswer: z
    .union([z.string(), z.array(z.union([z.string(), z.undefined()]))])
    .optional(),
  usedHint: z.boolean().optional(),
});

export const quizProgressSchema = z.object({
  grade: z.number().int().min(0),
  numQuestions: z.number().int().positive(),
  isComplete: z.boolean(),
  questionResults: z.array(questionResultSchema).optional(),
});

export const videoProgressSchema = z.object({
  played: z.boolean(),
  duration: z.number(),
  timeElapsed: z.number(),
  isComplete: z.boolean(),
});

export const introProgressSchema = z.object({
  worksheetAvailable: z.boolean().optional(),
  worksheetDownloaded: z.boolean().optional(),
  isComplete: z.boolean(),
});

export enum PostSubmissionState {
  SUBMISSION_STATE_UNSPECIFIED = "SUBMISSION_STATE_UNSPECIFIED",
  NEW = "NEW",
  CREATED = "CREATED",
  RECLAIMED_BY_STUDENT = "RECLAIMED_BY_STUDENT",
  TURNED_IN = "TURNED_IN",
  RETURNED = "RETURNED",
}

export const postSubmissionStateSchema = z.enum(PostSubmissionState);

// TurnedIn and Returned states are not updatable by our system, as they indicate that the student has turned in the assignment or the teacher has returned it, so we exclude those from the upsert options. Submission_State_Unspecified is also excluded as it's not a valid state for a submission.
export const UPDATABLE_SUBMISSION_STATES: PostSubmissionState[] = [
  PostSubmissionState.NEW,
  PostSubmissionState.CREATED,
  PostSubmissionState.RECLAIMED_BY_STUDENT,
];

const lessonProgressInputSchema = z.object({
  starterQuiz: quizProgressSchema.optional(),
  exitQuiz: quizProgressSchema.optional(),
  video: videoProgressSchema.optional(),
  intro: introProgressSchema.optional(),
});

const lessonProgressSchema = lessonProgressInputSchema.extend({
  updatedAt: z.string(),
  createdAt: z.string(),
});

export const pupilLessonProgressSchema = lessonProgressSchema.extend({
  postSubmissionState: postSubmissionStateSchema,
  submissionId: z.string(),
  attachmentId: z.string(),
  courseId: z.string(),
  itemId: z.string(),
  pupilLoginHint: z.string(),
});

export type PupilLessonProgress = z.infer<typeof pupilLessonProgressSchema>;

export const upsertPupilLessonProgressArgsSchema =
  lessonProgressInputSchema.extend({
    submissionId: z.string(),
    attachmentId: z.string(),
    courseId: z.string(),
    itemId: z.string(),
    pupilLoginHint: z.string(),
  });

export type UpsertPupilLessonProgressArgs = z.infer<
  typeof upsertPupilLessonProgressArgsSchema
>;

export const courseWorkPupilProgressSchema = lessonProgressSchema.extend({
  submissionId: z.string(),
  assignmentToken: z.string(),
  courseWorkId: z.string(),
  courseId: z.string(),
  pupilLoginHint: z.string(),
  gradeSubmitted: z.boolean().optional(),
  resultsLinkAttached: z.boolean().optional(),
  postSubmissionState: postSubmissionStateSchema.optional(),
});

export type CourseWorkPupilProgress = z.infer<
  typeof courseWorkPupilProgressSchema
>;

export const upsertCourseWorkPupilProgressArgsSchema =
  lessonProgressInputSchema.extend({
    submissionId: z.string(),
    assignmentToken: z.string(),
    courseWorkId: z.string(),
    courseId: z.string(),
    pupilLoginHint: z.string(),
  });

export type UpsertCourseWorkPupilProgressArgs = z.infer<
  typeof upsertCourseWorkPupilProgressArgsSchema
>;

export type CreateCourseWorkServiceArgs = {
  courseId: string;
  title: string;
  description?: string;
  lessonSlug: string;
  programmeSlug: string;
  unitSlug: string;
  maxPoints: number;
  teacherLoginHint: string;
};
